import * as Yup from "yup";
import * as uuid from "uuid";
import MuiStep from "@material-ui/core/Step";
import MuiStepper from "@material-ui/core/Stepper";
import StepContent from "@material-ui/core/StepContent";
import StepLabel from "@material-ui/core/StepLabel";
import React, { FC, useState } from "react";
import { Field, Form, Formik, FormikHelpers } from "formik";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";
import { useDispatch, useSelector } from "react-redux";

import AttachAssetsStep from "../../../CampaignCreationBasicSlides/AdGroupSelectionSlide/components/AdGroupCreationForm/steps/AttachAssetsStep";
import api from "../../../../api";
import { AdGroupType } from "../../../../types/adGroupTypes";
import { AdvertiserType } from "../../../../types/advertiserTypes";
import { CreativeAssetFromFile } from "../../../../types/creativeTypes";
import { ReduxApplicationState } from "../../../../types/ReduxTypes/ReduxApplicationState";
import {
  createAdGroup,
  createAdGroupTemplate,
  createCreativeTemplates,
  getAdGroupTemplates,
  uploadCreativeAssets,
} from "../../../../redux/reducers/campaignToCreate";
import { errorToaster, successToaster } from "../../../../helpers/toasters";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type CreateAdGroupTabProps = {
  selectedAdvertiser: AdvertiserType | null;
  isVideoCampaign: boolean;
  onCreated?: (templateId: string) => void;
};

type FormValues = {
  name: string;
  description: string;
  creativeAssets: CreativeAssetFromFile[];
};

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

const validationSchema = Yup.object({
  name: Yup.string().trim().required("Name is required."),
});

const STEPS = ["Name & Description", "Upload Assets"];

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const TabContent = styled("div")({
  display: "flex",
  flexDirection: "column" as const,
  gap: 24,
});

const StepperRoot = styled("div")(({ theme }: { theme: Theme }) => ({
  "& .MuiStepper-root": {
    padding: "0 0 8px 0",
    background: "transparent",
  },
  "& .MuiStepIcon-root": {
    color: theme.palette.brand.gray300,
  },
  "& .MuiStepIcon-active": {
    color: theme.palette.brand.coral,
  },
  "& .MuiStepIcon-completed": {
    color: theme.palette.brand.coral,
  },
  "& .MuiStepLabel-label": {
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.brand.gray500,
    fontFamily: "inherit",
  },
  "& .MuiStepLabel-active": {
    color: theme.palette.brand.gray900,
    fontWeight: 600,
  },
  "& .MuiStepLabel-completed": {
    color: theme.palette.brand.gray700,
  },
  "& .MuiStepContent-root": {
    borderLeft: `2px solid ${theme.palette.brand.gray200}`,
    marginLeft: 11,
    paddingLeft: 20,
    paddingRight: 0,
  },
  "& .MuiStepConnector-line": {
    borderColor: theme.palette.brand.gray200,
    borderLeftWidth: 2,
  },
}));

const StepPanel = styled("div")(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.white,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 10,
  padding: 20,
  marginTop: 8,
  marginBottom: 12,
}));

const FieldRow = styled("div")({
  marginBottom: 4,
});

const FieldLabel = styled("label")(({ theme }: { theme: Theme }) => ({
  display: "block",
  fontSize: 13,
  fontWeight: 500,
  color: theme.palette.brand.gray700,
  marginBottom: 6,
}));

const StyledInput = styled("input")(({ theme }: { theme: Theme }) => ({
  width: "100%",
  padding: "10px 12px",
  background: theme.palette.brand.white,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 8,
  fontSize: 14,
  color: theme.palette.brand.gray900,
  outline: "none",
  boxSizing: "border-box" as const,
  fontFamily: "inherit",
  transition: "border-color 0.15s",
  "&:focus": {
    borderColor: theme.palette.brand.blueAccent,
  },
  "&::placeholder": {
    color: theme.palette.brand.gray400,
  },
}));

const StyledTextarea = styled("textarea")(({ theme }: { theme: Theme }) => ({
  width: "100%",
  padding: "10px 12px",
  background: theme.palette.brand.white,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 8,
  fontSize: 14,
  color: theme.palette.brand.gray900,
  outline: "none",
  boxSizing: "border-box" as const,
  resize: "vertical" as const,
  minHeight: 80,
  fontFamily: "inherit",
  transition: "border-color 0.15s",
  "&:focus": {
    borderColor: theme.palette.brand.blueAccent,
  },
  "&::placeholder": {
    color: theme.palette.brand.gray400,
  },
}));

/** Fixed-height wrapper prevents layout shift when error appears/disappears */
const ErrorWrapper = styled("div")(({ theme }: { theme: Theme }) => ({
  minHeight: 30,
  fontSize: 12,
  color: theme.palette.brand.errorRed,
  paddingTop: 4,
}));

const DropzoneWrapper = styled("div")(({ theme }: { theme: Theme }) => ({
  "& .MuiDropzoneArea-root": {
    border: `2px dashed ${theme.palette.brand.gray200}`,
    borderRadius: 8,
    backgroundColor: theme.palette.brand.gray50,
    transition: "border-color 0.15s",
  },
  "& .MuiDropzoneArea-active": {
    borderColor: `${theme.palette.brand.coral} !important`,
    backgroundColor: theme.palette.brand.gray50,
  },
}));

const NavButtonRow = styled("div")({
  display: "flex",
  justifyContent: "flex-end",
  gap: 12,
  marginTop: 4,
});

const BackButton = styled("button")(({ theme }: { theme: Theme }) => ({
  padding: "10px 24px",
  background: "transparent",
  color: theme.palette.brand.gray700,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
  transition: "border-color 0.15s",
  "&:hover:not(:disabled)": {
    borderColor: theme.palette.brand.gray400,
  },
  "&:disabled": {
    opacity: 0.4,
    cursor: "not-allowed",
  },
}));

const NextButton = styled("button")(({ theme }: { theme: Theme }) => ({
  padding: "10px 28px",
  background: theme.palette.brand.coral,
  color: theme.palette.brand.white,
  border: "none",
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
  transition: "background 0.15s",
  "&:hover:not(:disabled)": {
    background: theme.palette.brand.coralHover,
  },
  "&:disabled": {
    opacity: 0.6,
    cursor: "not-allowed",
  },
}));

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const CreateAdGroupTab: FC<CreateAdGroupTabProps> = ({ selectedAdvertiser, onCreated }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const dispatch = useDispatch();
  const currentUser = useSelector((state: ReduxApplicationState) => state.user.currentUser);

  const initialValues: FormValues = {
    name: "",
    description: "",
    creativeAssets: [],
  };

  const handleSubmit = async (values: FormValues, { resetForm }: FormikHelpers<FormValues>) => {
    if (!selectedAdvertiser) {
      errorToaster("Please select an advertiser before creating an ad group.", 5000);
      return;
    }
    if (values.creativeAssets.length === 0) {
      errorToaster("Please upload at least one creative asset.", 5000);
      return;
    }

    const adGroupType = values.creativeAssets[0].fileType as AdGroupType;

    try {
      setIsLoading(true);

      // 1. Create ad group
      const adGroupBody = {
        adGroupId: `bg-ad-group-${uuid.v4()}`,
        brandgenAdvertiserId: selectedAdvertiser.brandgenAdvertiserId,
        brandgenCampaignPoolId: "",
        createdBy: currentUser,
        name: values.name,
        adGroupType,
        description: values.description ?? "",
      };
      const createdAdGroup = await createAdGroup(adGroupBody);

      if (createdAdGroup) {
        // 2. Create ad group template
        const templateBody = {
          id: `bg-ad-group-template-${uuid.v4()}`,
          brandgenAdvertiserId: createdAdGroup.brandgenAdvertiserId!,
          createdBy: currentUser,
          name: values.name,
          adGroupType,
          description: values.description,
        };
        const createdTemplate = await createAdGroupTemplate(templateBody);

        // 3. Upload creative assets
        const uploadedCreatives = await uploadCreativeAssets(
          values.creativeAssets,
          selectedAdvertiser.adLibAdvertiserId!,
          selectedAdvertiser.brandgenAdvertiserId,
          "https://www.domain.com",
          createdAdGroup.adGroupId,
          api.createCreative,
        );

        // 4. Create creative templates
        await createCreativeTemplates(uploadedCreatives, createdTemplate.id);

        successToaster("Ad group created successfully!", 4000);
        onCreated?.(createdTemplate.id);
      }
      resetForm();
      setActiveStep(0);
      // Refresh the saved ad groups list so it shows the new entry
      dispatch(getAdGroupTemplates(selectedAdvertiser.brandgenAdvertiserId));
    } catch (error) {
      errorToaster(error, 6000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnChange={false}
      validateOnBlur
    >
      {({ values, errors, touched, setFieldValue, setTouched, validateForm }) => (
        <Form>
          <TabContent>
            <StepperRoot>
              <MuiStepper activeStep={activeStep} orientation="vertical">
                {STEPS.map((label, index) => (
                  <MuiStep key={label}>
                    <StepLabel>{label}</StepLabel>
                    <StepContent>
                      <StepPanel>
                        {index === 0 && (
                          <>
                            <FieldRow>
                              <FieldLabel htmlFor="ads-upload-name">Name *</FieldLabel>
                              <Field name="name">
                                {({ field }: any) => (
                                  <StyledInput
                                    {...field}
                                    id="ads-upload-name"
                                    placeholder="Enter ad group name"
                                    autoComplete="off"
                                  />
                                )}
                              </Field>
                              <ErrorWrapper>{touched.name && errors.name ? errors.name : ""}</ErrorWrapper>
                            </FieldRow>

                            <FieldRow>
                              <FieldLabel htmlFor="ads-upload-description">Description</FieldLabel>
                              <Field name="description">
                                {({ field }: any) => (
                                  <StyledTextarea
                                    {...field}
                                    id="ads-upload-description"
                                    placeholder="Optional description"
                                    rows={3}
                                  />
                                )}
                              </Field>
                              <ErrorWrapper />
                            </FieldRow>
                          </>
                        )}

                        {index === 1 && (
                          <DropzoneWrapper>
                            <AttachAssetsStep
                              creativeAssets={values.creativeAssets}
                              setFieldValue={
                                setFieldValue as (fieldName: string, value: CreativeAssetFromFile[]) => void
                              }
                              limit={100}
                              isFromPage={false}
                            />
                          </DropzoneWrapper>
                        )}
                      </StepPanel>

                      <NavButtonRow>
                        <BackButton type="button" disabled={index === 0} onClick={() => setActiveStep((s) => s - 1)}>
                          Back
                        </BackButton>

                        {index < STEPS.length - 1 ? (
                          <NextButton
                            type="button"
                            onClick={async () => {
                              await setTouched({ name: true });
                              const errs = await validateForm();
                              if (!errs.name) {
                                setActiveStep((s) => s + 1);
                              }
                            }}
                          >
                            Next
                          </NextButton>
                        ) : (
                          <NextButton type="submit" disabled={isLoading}>
                            {isLoading ? "Creating…" : "Create Ad Group"}
                          </NextButton>
                        )}
                      </NavButtonRow>
                    </StepContent>
                  </MuiStep>
                ))}
              </MuiStepper>
            </StepperRoot>
          </TabContent>
        </Form>
      )}
    </Formik>
  );
};

export default CreateAdGroupTab;
